import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { teamMembers } from '@/mocks/team';
import { testimonials } from '@/mocks/testimonials';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      setIsSubmitting(false);
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rdd27nahb7382ovku0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        if (formStatus === 'success') setFormStatus('idle');
        if (formStatus === 'error') setFormStatus('idle');
      }, 5000);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://shaoshangliang.cn/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://shaoshangliang.cn/about' },
        ],
      },
      {
        '@type': 'AboutPage',
        name: '关于久久爱 - 让爱长久的情感陪伴平台',
        description: '久久爱是专为渴望长久爱情的人打造的情感陪伴平台。了解我们的品牌故事、专业团队和核心服务。',
        url: 'https://shaoshangliang.cn/about',
      },
      {
        '@type': 'Organization',
        name: '久久爱',
        url: 'https://shaoshangliang.cn/',
        description: '久久爱是专为渴望长久爱情的人打造的情感陪伴平台，提供专业情感指导、伴侣匹配与深度陪伴服务。',
        foundingDate: '2018',
        founders: [{ '@type': 'Person', name: '林婉清' }],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'hello@jiujiuai.com',
          availableLanguage: ['Chinese'],
        },
      },
    ],
  };

  return (
    <div className="pt-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />

        {/* Hero */}
        <section className="mt-8 mb-16">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
            关于久久爱
          </h1>
          <p className="text-lg text-foreground-500 max-w-3xl">
            久久爱，让每一段爱都值得被珍惜与守护
          </p>
        </section>

        {/* Brand Story */}
        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-5">品牌故事</h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  久久爱诞生于2018年，由心理学博士林婉清女士创立。在长达十年的情感咨询工作中，林婉清见证了太多因为缺乏正确引导而错失真爱的故事，也看到了太多因为不懂经营而破碎的美好感情。
                </p>
                <p>
                  她深信，每一段感情都值得被认真对待，每一个人都拥有获得长久幸福的能力。带着这份信念，久久爱从一个小小的情感咨询工作室起步，逐步发展成为一个集<strong>情感指导</strong>、<strong>伴侣匹配</strong>、<strong>感情修复</strong>和<strong>长久陪伴</strong>于一体的综合性情感服务平台。
                </p>
                <p>
                  如今，久久爱已服务超过五万名用户，见证了无数从相遇到相守的美好故事。我们的团队汇聚了心理学、社会学、人工智能等多个领域的专业人才，致力于用科学与温暖，守护每一份真心。
                </p>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden h-80 lg:h-96">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cozy%20modern%20office%20space%20with%20soft%20coral%20pink%20accents%20natural%20light%20streaming%20through%20large%20windows%20comfortable%20armchairs%20plants%20bookshelves%20peaceful%20atmosphere%20editorial%20interior%20design%20clean%20minimal%20style&width=800&height=600&seq=about-brand-story&orientation=landscape"
                alt="久久爱温馨的办公环境"
                title="久久爱品牌故事 - 让爱长久的情感陪伴平台"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="mb-20 py-12 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6 rounded-2xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
              使命与价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mb-4">
                  <i className="ri-focus-3-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  用科学与温暖，帮助每一个人找到真爱、维系感情、修复关系，让每一段爱都能长久绽放。
                </p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为中国最值得信赖的情感陪伴平台，让每一个渴望爱的人都能在这里找到归属和幸福。
                </p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
                  <i className="ri-heart-3-line text-xl text-secondary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">核心价值观</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  真诚、专业、温暖、陪伴。我们相信每一份感情都值得被尊重，每一个用户都值得被温柔以待。
                </p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">品质承诺</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  所有情感咨询师均持证上岗，匹配算法经过严格验证，服务流程透明规范，确保每一位用户都能获得高品质的情感服务体验。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="mb-20">
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">核心团队</h2>
          <p className="text-sm text-foreground-500 mb-10 text-center max-w-lg mx-auto">
            一群热爱生活、相信爱情的人，用专业与用心守护每一份真心
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="bg-background-50 rounded-xl overflow-hidden border border-background-200/70 text-center group"
              >
                <div className="h-52 overflow-hidden">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    title={`${member.name} - 久久爱${member.role}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-base font-semibold text-foreground-900">{member.name}</h3>
                  <p className="text-sm text-primary-500 font-medium mt-1">{member.role}</p>
                  <p className="text-xs text-foreground-500 mt-3 leading-relaxed">{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="mb-20">
          <div className="bg-background-100 rounded-2xl py-12 px-4 md:px-8">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">用户口碑</h2>
            <p className="text-sm text-foreground-500 mb-10 text-center max-w-lg mx-auto">
              听听用户们怎么说——每一段评价都是我们前进的动力
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {testimonials.slice(0, 6).map((t) => (
                <div key={t.id} className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < t.rating ? 'ri-star-fill text-secondary-500' : 'ri-star-fill text-background-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img src={t.avatar} alt={t.name} className="w-9 h-9 rounded-full object-cover" />
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.city} &middot; {t.age}岁</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="mb-10 scroll-mt-20">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">联系我们</h2>
            <p className="text-sm text-foreground-500 mb-8 text-center">
              有任何问题或建议？请填写以下表单，我们会尽快回复您
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="bg-background-100 rounded-2xl p-6 md:p-10 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  咨询类型 <span className="text-primary-500">*</span>
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                >
                  <option value="">请选择咨询类型</option>
                  <option value="情感指导">情感指导</option>
                  <option value="伴侣匹配">伴侣匹配</option>
                  <option value="感情修复">感情修复</option>
                  <option value="长久陪伴">长久陪伴</option>
                  <option value="其他">其他咨询</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请描述您的问题或需求（最多500字）"
                ></textarea>
              </div>

              {/* Honeypot */}
              <div className="website-alt-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="flex items-start gap-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center px-8 py-3 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-all cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? '提交中...' : '提交留言'}
                </button>
              </div>

              {formStatus === 'success' && (
                <div className="p-4 rounded-lg bg-accent-100 text-accent-700 text-sm">
                  感谢您的留言！我们会尽快与您取得联系。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-4 rounded-lg bg-primary-100 text-primary-700 text-sm">
                  {formError}
                </div>
              )}
            </form>
          </div>
        </section>

        {/* Contact Info */}
        <section className="mb-16 text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-6 text-sm text-foreground-500">
            <span className="flex items-center gap-2">
              <i className="ri-mail-line text-primary-500"></i>
              hello@jiujiuai.com
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-phone-line text-primary-500"></i>
              400-888-9999
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-map-pin-line text-primary-500"></i>
              北京市朝阳区建国路88号久久爱大厦
            </span>
          </div>
        </section>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </div>
  );
}